import { useEffect, useState } from "react";
import useLiveSiteMap from "../../hooks/useLiveSiteMap";
import useLiveTrackData from "../../hooks/useLiveTrackData";
import { findCurrentPigPosition } from "../../utils/pigUtils";
import { convertToLocalTime } from "@utils/dateAndTimeUtils";
import { Spinner } from "@components/ui/spinner/Spinner";

function elapsedTimeToHHMMss(endTimestamp, startTimestamp) {  
    if(!endTimestamp || !startTimestamp) return null

    //calculate the difference in seconds
    const diffInSeconds = Math.floor((endTimestamp - startTimestamp) / 1000);
    
    //convert seconds to hours and minutes
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    //format to HH:mm
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Calculated Live Track data including next AGM, time elapsed, speed, etc. Can be used on any page that needs live updates of a run, like Live Track Edit, Live Track, Site Map w/PIG.
 * 
 * @param {Number} selectedJob
 * @param {Number} selectedRun 
 * @param {Date} dataUpdated - whenever this is updated in the parent component, this component will check for new live track data and recalculate state
 * 
 * @returns {JSX.Element}

 */
export const LiveTrackSummary = ({selectedJob, selectedRun, dataUpdated, timezone}) => {
  //state to display
  const [currentSpeed, setCurrentSpeed] = useState()
  const [lastAGM, setLastAGM] = useState()
  const [nextAGM, setNextAGM] = useState()
  const [timeToNextAGM, setTimeToNextAGM] = useState()
  const [elapsedTime, setElapsedTime] = useState()
  const [timeUntilComplete, setTimeUntilComplete] = useState()
  const [timeRunComplete, setTimeRunComplete] = useState()
  
  const [loading, setLoading] = useState(true)

  //state to keep track of run progress, change what we show depending on these values
  const [isRunComplete, setIsRunComplete] = useState()
  const [hasRunLaunched, setHasRunLaunched] = useState(false)

  //need formattedLiveTrackTable to get up-to-date estimated pass times
  const { 
    fetchLiveTrackData,
    formattedLiveTrackTable,
    checkLiveTrackForUpdates
  } = useLiveTrackData();

  //using mergedLTandRoute to get current pig data from pigUtils - it's used as an argument of the findCurrentPigPosition function elsewhere in martrak
  const {
    mergeLiveTrackWithRouteArr,
    findAndStoreLastUpdateValue,
    mergedLTandRoute,
    fetchRouteArr,
  } = useLiveSiteMap();

  //useEffects

  //On job Select - fetch the route array (api call and merge)
  useEffect(() => {
    if (selectedJob) {
      fetchRouteArr(selectedJob);
    }
  }, [selectedJob]); //eslint-disable-line

  //On run select - fetch the live track data
  useEffect(() => {
    if (selectedRun) {
      setLoading(true);
      fetchLiveTrackData(selectedRun);
      checkLiveTrackForUpdates(selectedRun);

    }
  }, [selectedRun]); //eslint-disable-line

  //when data updated in parent (pass time entered or time interval repeats), check for live track updates
  useEffect(() => {
    if (selectedRun) {
      checkLiveTrackForUpdates(selectedRun);
    }
  }, [dataUpdated]); //eslint-disable-line

  //update route arr when new formattedLiveTrackTable data comes in
  useEffect(() => {
    mergeLiveTrackWithRouteArr(formattedLiveTrackTable);
    findAndStoreLastUpdateValue(formattedLiveTrackTable);
  }, [formattedLiveTrackTable]); //eslint-disable-line

  //recalculate data when live track data changes
  useEffect(() => {

    if(mergedLTandRoute.length > 0 && formattedLiveTrackTable.length > 0){
        const launchTime = new Date(mergedLTandRoute[0].PassTime).getTime()

        if(!launchTime) return
          
        setHasRunLaunched(true)

        const currentTime = new Date().getTime();

        //find the estimated pass time, or actual pass time, for final agm (pass time may not have been entered by users, so use estimated pass time as backup)
        const finalAGM = formattedLiveTrackTable[formattedLiveTrackTable.length - 1];
        const finalAGMEstPassTimestamp = new Date(finalAGM?.Est_PassTime)?.getTime() || 0;
        const finalAGMPassTimeTimestamp = new Date(finalAGM?.PassTime)?.getTime() || 0;

        const runIsComplete = !!finalAGMPassTimeTimestamp || currentTime > finalAGMEstPassTimestamp;

        //calculate state differently depending on whether run is complete or in progress
        if(runIsComplete){
          setTimeUntilComplete(0)
          setTimeRunComplete(convertToLocalTime(finalAGM?.PassTime ?? finalAGM?.Est_PassTime, timezone.value))
          setTimeToNextAGM(0)

          const completeElapsedTime = elapsedTimeToHHMMss(finalAGMPassTimeTimestamp || finalAGMEstPassTimestamp, launchTime)
          setElapsedTime(completeElapsedTime)
          
          setIsRunComplete(true)

        } else {
          //get current pig data
          const currentPigData = findCurrentPigPosition(mergedLTandRoute.filter(item => item.AGMID), currentTime);

          //to avoid crashing the page when trying to get data out of currentPigData - happened once or twice where live track summary seemed to crash the page
          //need better solution for this
          if(!currentPigData) return
          
          //set speed and previous/next agm
          setCurrentSpeed(currentPigData.speed)
          setLastAGM(currentPigData.lastPoint)
          setNextAGM(currentPigData.nextPoint)

          //calculate time until complete
          setTimeUntilComplete(elapsedTimeToHHMMss(finalAGMEstPassTimestamp, currentTime))
  
          //calculate time run will be compelte
          setTimeRunComplete(convertToLocalTime(finalAGM?.Est_PassTime, timezone.value))
  
          //get time to next agm
          const nextAGMRaw = formattedLiveTrackTable.find(agm => agm.AGMID === currentPigData.nextPoint?.AGMID)
          const nextAGMRawEstPassTimestamp  = new Date(nextAGMRaw?.Est_PassTime)?.getTime() || 0

          
          setTimeToNextAGM(elapsedTimeToHHMMss(nextAGMRawEstPassTimestamp, currentTime))

          //get total elapsed time for run
          setElapsedTime(elapsedTimeToHHMMss(currentTime, launchTime))
          setIsRunComplete(false)

        }
        
        setLoading(false)
    } else{
      setHasRunLaunched(false)
    }

  }, [mergedLTandRoute, formattedLiveTrackTable])

  // show spinner while we're waiting for initial live data to load
  if (loading) {
    return (
      <div className="flex justify-center items-center py-6 h-56">
        <Spinner className="text-2xl" />
        <span className="ml-2">Loading live track summary...</span>
      </div>
    )
  }

  //if pig hasn't launched yet, no need to show data summary
  if(!hasRunLaunched){
    return null
  }

  return(
    <div className="mt-8 mb-16">

      {/* if run complete, only show the total time elapsed and time run was completed */}
      {isRunComplete ? (
          <div className="grid grid-cols-[minmax(100px,200px)_auto] gap-x-4 gap-y-2 items-center">
            <span className="font-semibold text-gray-700">Total elapsed time:</span>
            <span>{elapsedTime}</span>

            <span className="font-semibold text-gray-700">Time of completion:</span>
            <span>{timeRunComplete} {`(${timezone.abbr})`}</span>
          </div>
        )

        //if run is ongoing, show extra info
        : (

          <div className="grid grid-cols-[minmax(100px,300px)_auto] gap-x-4 gap-y-2 items-center">
            <span className="font-semibold text-gray-700">Current speed:</span>
            <span>{currentSpeed} km/h</span>

            <span className="font-semibold text-gray-700">Previous AGM:</span>
            <span>{lastAGM?.Site_Name}</span>

            {nextAGM && (
              <>
                <span className="font-semibold text-gray-700">Next AGM:</span>
                <span>{nextAGM?.Site_Name}</span>

                <span className="font-semibold text-gray-700">Next AGM ETA:</span>
                <span>{timeToNextAGM}</span>
              </>
            )}

            <span className="font-semibold text-gray-700">Estimated time until run complete:</span>
            <span>{timeUntilComplete}</span>

            <span className="font-semibold text-gray-700">Estimated time of completion:</span>
            <span className="whitespace-nowrap">{timeRunComplete}</span>
          </div>

        )
      }
    </div>
    
  )
}